import React, { useState, useEffect } from "react";
import styled from "styled-components";
import StarIcon from "@mui/icons-material/Star";
import ButtonIcon from "../../styles/ButtonIcon";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import { motion, AnimatePresence } from "framer-motion";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../../authentication/firebase";
import { CircularProgress } from "@mui/material";

const Testimonials = () => {
  const [active, setActive] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch testimonials from Firebase
  const fetchTestimonialsFromFirebase = async () => {
    try {
      setLoading(true);
      
      const testimonialsCollectionRef = collection(db, "testimonials");
      const testimonialsQuery = query(testimonialsCollectionRef, orderBy("order", "asc"));
      const testimonialsSnapshot = await getDocs(testimonialsQuery);
      
      const fetchedTestimonials = testimonialsSnapshot.docs.map(doc => ({
        firebaseId: doc.id,
        ...doc.data()
      }));
      
      setTestimonials(fetchedTestimonials);
      
      // Reset active index if it's out of bounds
      if (fetchedTestimonials.length > 0 && active >= fetchedTestimonials.length) {
        setActive(0);
      }
      
    } catch (error) {
      console.error("Error fetching testimonials from Firebase:", error);
      setError("Failed to load testimonials. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonialsFromFirebase();
  }, []);

  const next = () => setActive((prev) => (prev + 1) % testimonials.length);
  const prev = () =>
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="inherit" size={50} />
        <LoadingText>Loading testimonials...</LoadingText>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer>
        <p>{error}</p>
        <button onClick={fetchTestimonialsFromFirebase} className="primary-btn">
          Try Again
        </button>
      </ErrorContainer>
    );
  }

  if (testimonials.length === 0) {
    return (
      <NoDataContainer>
        <NoDataMessage>
          No testimonials available at the moment. Check back later!
        </NoDataMessage>
      </NoDataContainer>
    );
  }

  return (
    <Styled>
      <motion.div 
        className="content"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.div 
          className="left"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2>
            Transforming Change,
            <br />
            Creating a Greener Future
          </h2>
          <p>Look at what people say about us!</p>
        </motion.div>

        <motion.div 
          className="right"
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              className="testimonial"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h4>{testimonials[active].title}</h4>
              <p className="text">{testimonials[active].quote}</p>

              <div className="info">
                <img src={testimonials[active].avatar} alt="avatar" />
                <div>
                  <span className="name">{testimonials[active].name}</span>
                  <div className="stars">
                    {Array.from({ length: testimonials[active].rating }).map(
                      (_, i) => (
                        <span key={i}>
                          <StarIcon htmlColor="#FFC300" />
                        </span>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="controls">
                <div className="dots">
                  {testimonials.map((_, i) => (
                    <motion.span
                      key={i}
                      className={i === active ? "dot active" : "dot"}
                      animate={{
                        backgroundColor: i === active ? "var(--primary-color)" : "#ccc"
                      }}
                      transition={{ duration: 0.3 }}
                      onClick={() => setActive(i)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </div>
                <div className="arrows">
                  <ButtonIcon onClick={prev}>
                    <WestIcon fontSize="small" />
                  </ButtonIcon>
                  <ButtonIcon onClick={next}>
                    <EastIcon fontSize="small" />
                  </ButtonIcon>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </Styled>
  );
};

export default Testimonials;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;
`;

const LoadingText = styled.p`
  color: var(--text-color);
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  gap: 20px;
  text-align: center;
  width: var(--section-width);
  margin: 0 auto;
  
  p {
    color: var(--text-color);
    font-size: 1.1rem;
    margin: 0;
  }
  
  button {
    padding: 12px 24px;
    border-radius: 50px;
    border: none;
    background: var(--primary-color);
    color: var(--text-color);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const NoDataContainer = styled.div`
  width: var(--section-width);
  margin: 0 auto;
  padding: 60px 0;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-gray-color);
  font-size: 1.1rem;
  background: var(--section-bg-color);
  border-radius: 12px;
`;

const Styled = styled.section`
  width: var(--section-width);
  margin: var(--section-margin) auto;
  .content {
    margin: 0 auto;
    display: flex;
    gap: 4rem;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;

    .left {
      flex: 1 1 40%;

      h2 {
        font-size: 2rem;
        color: #1c3c24;
        margin-bottom: 0.5rem;
      }

      p {
        color: #666;
        font-size: 1rem;
      }
    }

    .right {
      flex: 1 1 40%;

      .testimonial {
        background: #f9f9f9;
        border-radius: 16px;
        padding: 2rem;
        position: relative;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);

        .quote-icon {
          position: absolute;
          top: 1rem;
          left: 1rem;
          color: var(--primary-color);
          opacity: 0.2;
          font-size: 3rem;
        }

        h4 {
          font-size: 1.2rem;
          color: #1c3c24;
          margin-bottom: 0.5rem;
        }

        .text {
          color: #555;
          font-size: 0.95rem;
          margin-bottom: 1.5rem;
        }

        .info {
          display: flex;
          align-items: center;
          gap: 1rem;

          img {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            object-fit: cover;
          }

          .name {
            font-weight: bold;
            color: #1c3c24;
          }

          .stars {
            color: #ffc107;
            font-size: 1rem;
          }
        }

        .controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 1.5rem;

          .dots {
            display: flex;
            gap: 0.5rem;

            .dot {
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background: #ccc;

              &.active {
                background: var(--primary-color);
              }
            }
          }

          .arrows {
            display: flex;
            gap: 12px;
            button {
              border: 1px solid var(--border-color);
              border-radius: 50px;
              padding: 0.4rem;
              cursor: pointer;
              transition: all 0.2s ease;
              color: var(--text-light-color);

              &:hover {
                border-color: var(--primary-color);
                color: var(--primary-color);
              }
            }
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .content {
      flex-direction: column;
    }
  }
`;
